import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ProgramDetailResponse } from "../program.types";
import { USER_PROGRAMS_API } from "../program.constants";

/**
 * Arguments for useFetchUserProgramDetail hook.
 */
export type UseFetchUserProgramDetailArgs = {
  /** The program ID to fetch */
  programId: number | string | undefined;
  /** Whether the query is enabled */
  isEnabled?: boolean;
  /** Whether the store has been hydrated from localStorage */
  isHydrated?: boolean;
  /** Refetch on window focus */
  refetchOnWindowFocus?: boolean;
  /** Refetch on reconnect */
  refetchOnReconnect?: boolean;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
};

/**
 * Hook to fetch a single program's detail for the authenticated user.
 * Returns React Query result directly - this is the source of truth for program data.
 *
 * @example
 * ```typescript
 * // In a component
 * const { id } = useParams<{ id: string }>();
 * const { data, isLoading } = useFetchUserProgramDetail({
 *   programId: id,
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 *
 * return <ProgramDetail program={data?.program} />;
 * ```
 */
export function useFetchUserProgramDetail({
  programId,
  isEnabled = true,
  isHydrated = true,
  refetchOnWindowFocus = true,
  refetchOnReconnect = true,
  user,
}: UseFetchUserProgramDetailArgs) {
  return useQuery<ProgramDetailResponse, Error>({
    queryKey: ["userProgramDetail", programId],
    queryFn: async () => {
      return apiRequest(
        `${USER_PROGRAMS_API}${programId}/`,
        "GET"
      ) as Promise<ProgramDetailResponse>;
    },
    // Only enable the query when store is hydrated, user is logged in, and programId exists
    enabled: !!user && isHydrated && isEnabled && !!programId,
    refetchOnWindowFocus,
    refetchOnReconnect,
  });
}

export default useFetchUserProgramDetail;

